import DropZone from '@/components/ui/dropZone';
import { INodeTemplate } from '@/types/node';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
} from '@mui/material';
import { Theme } from '@mui/material/styles';
import React, {
  cloneElement,
  forwardRef,
  isValidElement,
  useContext,
  useImperativeHandle,
  useState,
} from 'react';
import { Controller, FormProvider, useForm } from 'react-hook-form';
import { getImage } from '@shared/utils/imageFormat';
import { EditNodeTemplateRef, IEditNodeTemplateOptions } from '.';
import { InputControl } from '@/components/input-control';
import { MediaPlaceholder } from '@/components/media-placeholder';
import { SwitchControl } from '@/components/switch-control';
import { AuthContext, ManagerConferenceContext } from '@/store';

const useStyles = () => ({
  dialog: {},
  scrollPaper: {
    justifyContent: 'flex-start !important',
  },
  img: {
    margin: 'auto !important',
    width: '90px !important',
    height: '90px !important',
    backgroundColor: '#666 !important',
    backgroundRepeat: 'no-repeat !important',
    backgroundPosition: 'center !important',
    backgroundSize: 'contain !important',
  },
  buttons: {
    display: 'flex !important',
    alignItems: 'center !important',
    width: '100% !important',
  },
  dropZone: {
    backgroundColor: '#e0e0e0 !important',
    display: 'inline-block !important',
    '&:hover': {
      backgroundColor: '#d5d5d5 !important',
    },
  },
  btnStyle: {
    color: 'rgba(0, 0, 0, 0.87) !important',
    '&:hover': {
      backgroundColor: '#d5d5d5 !important',
    },
  },
});

const defaultValues = {
  id: '',
  title: '',
  description: '',
  picture: '',
  public: false,
};
export const EditNodeTemplate = forwardRef<EditNodeTemplateRef>(({}, ref) => {
  const classes = useStyles();

  const [isOpen, setIsOpen] = useState<boolean>(false);
  const [options, setOptions] = useState<IEditNodeTemplateOptions | null>(null);
  const { authState } = useContext(AuthContext);
  // ManagerConferenceContext has no default and is only provided inside
  // conference-scoped routes - guard against a crash if this dialog is ever
  // rendered outside of one.
  const { conferenceData } = useContext(ManagerConferenceContext) ?? {};
  const formMethods = useForm({
    defaultValues: defaultValues,
  });

  const { reset, getValues, watch } = formMethods;
  const values = getValues();
  const watchTitle = watch('title', values.title);
  useImperativeHandle(ref, () => ({
    open: (initOptions: IEditNodeTemplateOptions) => {
      setOptions(initOptions);
      reset(initOptions.data || defaultValues);
      setIsOpen(true);
    },
  }));

  const cleanUp = () => {
    setIsOpen(false);
    setOptions(null);
    reset();
  };

  const handleSubmit = () => {
    if (typeof options.saveAsTemplate === 'function') {
      options.saveAsTemplate(getValues(), conferenceData?.conference?.id);
    }
    cleanUp();
  };

  return (
    <Dialog
      open={isOpen}
      onClose={cleanUp}
      maxWidth={'sm'}
      fullWidth={true}
      // classes={{ paper: classes.dialog, scrollPaper: classes.scrollPaper }}
      slotProps={{
        paper: { sx: classes.dialog },
        root: { sx: classes.scrollPaper },
      }}
    >
      <DialogTitle>
        Save this (and everything inside) as a template?
      </DialogTitle>
      <DialogContent>
        <FormProvider {...formMethods}>
          <Box display="flex" alignItems="flex-start">
            <Box>
              <Controller
                name="picture"
                defaultValue={values.picture}
                render={({ field: { onChange, value } }) => (
                  <Box pr={1} textAlign="center">
                    <Box
                      sx={classes.img}
                      style={{
                        backgroundImage: value
                          ? `url(${getImage(value, 'small', null, 'managers')})`
                          : '',
                      }}
                    />
                    <br />
                    <DropZone
                      acceptedfiletypes="image/jpeg, image/png, image/webp"
                      uploadHandler={(uploadInfo: any) => {
                        onChange(uploadInfo.generated);
                      }}
                      buttonText={'Upload Image'}
                      uploadurl={'/api/managersignmedia'}
                      sx={classes.dropZone}
                    />
                  </Box>
                )}
              />
            </Box>
            <Box flexGrow={1}>
              <Box pt={1} pb={1}>
                <InputControl
                  name="title"
                  label="Title"
                  defaultValue={values.title}
                  fullWidth
                  variant="outlined"
                />
              </Box>
              <Box pb={1}>
                <InputControl
                  name="description"
                  label="Description"
                  defaultValue={values.description}
                  fullWidth
                  variant="outlined"
                />
              </Box>
              {authState.user.access_level === 400 && (
                <Box pb={1}>
                  <SwitchControl
                    name="public"
                    label="In public library?"
                    defaultValue={values.public}
                    options={[true, false]}
                  />
                </Box>
              )}
            </Box>
          </Box>
        </FormProvider>
      </DialogContent>
      <DialogActions>
        <Box sx={classes.buttons} justifyContent="flex-end">
          <Button onClick={cleanUp} sx={classes.btnStyle}>
            Cancel
          </Button>
          <Button disabled={!watchTitle} color="primary" onClick={handleSubmit}>
            {values.id ? 'Update' : 'Save'}
          </Button>
        </Box>
      </DialogActions>
    </Dialog>
  );
});
